import dayjs from "dayjs";

import api from "services/api";

export interface IHearingCustomField {
  id: number;
  name: string;
  type: "int" | "bool" | "string" | "date" | "datetime";
  active: boolean;
  customerId: number;
  index: number;
  size: number;
  linkedProperty: string | null;
  linkedBehavior: string | null;
  component: "TextInput" | "RadioButton" | "DateInput";
  container: string;
  defaultValue: string | null;
  required: boolean;
}

export interface IHearingCustomFieldValue {
  hearingCustomFieldId: number;
  value: string | boolean | number;
}

export const getRequiredCustomFieldErrors = (
  fields: IHearingCustomField[],
  values: Record<number, string | boolean | number>,
): Record<number, boolean> => {
  const errors: Record<number, boolean> = {};

  fields.forEach((field) => {
    if (!field.required) return;

    if (field.component === "RadioButton") {
      errors[field.id] = !fields.some(
        (option) =>
          option.component === "RadioButton" &&
          (option.container || "default") === (field.container || "default") &&
          values[option.id] === true,
      );
      return;
    }

    const value = values[field.id];
    errors[field.id] =
      value === undefined || value === null ||
      (typeof value === "string" && value.trim() === "");

    if (
      field.component === "DateInput" ||
      field.type === "date" ||
      field.type === "datetime"
    ) {
      // Compare the calendar date as well: parsing alone can normalize invalid days.
      const date = typeof value === "string" ? dayjs(value) : null;
      errors[field.id] =
        !date?.isValid() ||
        typeof value !== "string" ||
        !/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(value) ||
        dayjs(value.slice(0, 10)).format("YYYY-MM-DD") !== value.slice(0, 10);
    }
  });

  return errors;
};

export const HearingCustomFieldService = {
  async getAll(
    customerId: number,
    active?: number | null,
  ): Promise<IHearingCustomField[]> {
    try {
      const params = new URLSearchParams();
      params.append("customerId", String(customerId));
      if (active !== null && active !== undefined) {
        params.append("active", String(active));
      }

      const response = await api.get(
        `/v1/HearingCustomField/GetAllByCustomerId?${params.toString()}`,
      );

      return response.data.data ?? [];
    } catch (error) {
      console.error("Error fetching hearing custom fields:", error);
      return [];
    }
  },
};
